import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";
import ChatHeader from "./ChatHeader";
import EmptyChatState from "./EmptyChatState";
import useChatWebSocket from "../../hooks/useChatWebSocket";
import { useAuth } from "../../contexts/AuthContext";
import { CircularProgress, Typography, IconButton } from "@mui/material";
import { X, Reply } from "lucide-react";

const ChatArea = ({ user }) => {
  const { user: authUser } = useAuth();
  const roomId = user?.chat_id;

  const {
    messages,
    sendMessage,
    deleteMessage,
    togglePinMessage,
    toggleStarMessage,
    replyingTo,
    setReplyingTo,
    isConnected,
    isLoadingHistory
  } = useChatWebSocket(
    roomId,
    authUser?.id
  );

  const handleSubmit = (data) => {
    sendMessage(data.message);
  };

  if (!user) return <EmptyChatState />;

  return (
    <div className="flex flex-col h-full w-full pb-2 overflow-hidden bg-[#efe7de]">
      <ChatHeader user={user} />

      <div className="flex items-center gap-1 px-4 pt-1 bg-white/50">
        <span
          className={`inline-block w-2 h-2 rounded-full ${isConnected ? "bg-green-500" : "bg-gray-400"
            }`}
        />
        <Typography variant="caption" color="textSecondary">
          {isConnected ? "Connected" : "Connecting..."}
        </Typography>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4 mt-2 mr-1">
        {isLoadingHistory ? (
          <div className="flex justify-center items-center h-full">
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            {messages.length === 0 && (
              <Typography
                className="text-center text-gray-400 mt-10"
                variant="body2"
              >
                No messages yet. Say hello! 👋
              </Typography>
            )}
            <ChatMessages
              messages={messages}
              deleteMessage={deleteMessage}
              togglePinMessage={togglePinMessage}
              toggleStarMessage={toggleStarMessage}
              setReplyingTo={setReplyingTo}
            />
          </>
        )}
      </div>

      {/* Reply Preview Bar */}
      {replyingTo && (
        <div className="mx-4 mb-1 p-2 bg-white border-l-4 border-[#075e54] rounded-md shadow-sm animate-in slide-in-from-bottom-2 duration-200">
          <div className="flex justify-between items-start">
            <div className="flex gap-2 items-center overflow-hidden">
              <Reply size={14} className="text-[#075e54] shrink-0" />
              <div className="overflow-hidden">
                <div className="text-[12px] font-bold text-[#075e54]">
                  Replying to {replyingTo.role === 'me' ? 'yourself' : 'others'}
                </div>
                <div className="text-[13px] text-gray-500 truncate mr-2 italic">
                  {replyingTo.content}
                </div>
              </div>
            </div>
            <IconButton size="small" onClick={() => setReplyingTo(null)} sx={{ mt: -0.5 }}>
              <X size={16} />
            </IconButton>
          </div>
        </div>
      )}

      <ChatInput onSubmit={handleSubmit} />
    </div>
  );
};

export default ChatArea;
